import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { WxLabel, WxPrice } from '../..';
import { ProductCardType } from '../../../hooks/api/queries/useProducts';
import { IMAGE_RATIOS } from '../../../styles/imageRatios';
import { appendCommitHash } from '../../../utils/appendHash';
import { FavoriteIcon } from '../../ui/favorites/FavoriteIcon';
import { WImage } from '../../ui/image/Image';
import { PriceImage } from '../../ui/image/PriceImage';

type Props = {
  product: ProductCardType & { sorting?: number };
  categorySlug?: string;
};

export const ProductCard = React.memo(
  ({ product, categorySlug }: Props) => {
    const price = product.priceOverwrite
      ? product.priceOverwrite?.milesAmount
      : product.price?.milesAmount;

    const priceBefore = product.priceOverwrite
      ? product.price?.milesAmount
      : undefined;

    return (
      <Container>
        <AbsolutePosition>
          <FavoriteIcon productId={product.id} />
        </AbsolutePosition>
        <Link
          to={appendCommitHash(`/product/${product.slug}`)}
          state={{ categorySlug }}
        >
          <ImageDiv>
            <PriceImage price={product.priceOverwrite} />
            <WImage
              image={product.localizedImage ?? product.defaultImage}
              background
            />
          </ImageDiv>
          <Bottom hasPriceBefore={Boolean(priceBefore)}>
            <WxLabel label={product.name} black hover={false} />
            <WxPrice price={price} priceBefore={priceBefore} />
          </Bottom>
        </Link>
      </Container>
    );
  },
  (prev, next) => prev.product.id === next.product.id,
);

const Container = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.white};
`;

const AbsolutePosition = styled.div`
  position: absolute;
  z-index: 1;
  top: 1.25rem;
  right: 1.25rem;
`;

const ImageDiv = styled.div`
  position: relative;

  img {
    width: 100%;
    aspect-ratio: ${IMAGE_RATIOS.PRODUCT_LIST};
    object-fit: cover;
  }
`;

const Bottom = styled.div<{ hasPriceBefore: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  height: 72px;
  padding-inline: 1.25rem;

  > :first-child {
    max-width: 19ch;
  }

  > :last-child {
    ${({ hasPriceBefore }) =>
      hasPriceBefore &&
      css`
        align-self: flex-start;
        margin-top: 8px;
      `}
  }
`;
